import React from "react";
import { SubmitPicks, SubmitHorseRacePicks } from "@freeplay/shared-code";
import { useReducer } from "react";
import { GameView } from "../views/game/GameView";
import { createPlayerBetDTO, createPlayerBetDTOWithHorseRacesPicks } from "../utils/dtoHelper";
import { useScrollToTop } from "../components/hooks/scrollToTopHook";
import { useEvent } from "../contexts/EventContext";
import { MixPanelEventEnum, useMixpanel } from "../utils/analytics";

export const GameController = (): JSX.Element => {
    const { currentEvent, sendPicks } = useEvent();
    const [state, dispatch] = useReducer(picksReducer, {
        choice: { platformEventId: 0, platformOfferId: 0, platformOutcomeId: 0 },
        choices: [],
        horseRacePick: { choices: [] },
        horseRaceChoices: [],
        randomButtonPressed: false,
        favoritesButtonPressed: false,
    });

    useScrollToTop();
    useMixpanel(MixPanelEventEnum.MAKE_PICK_SCREEN_VIEWED, currentEvent?.id ?? -1);

    if (!currentEvent) {
        return <></>;
    }

    const handleSendPicks = () => {
        if (currentEvent.horseRacingPrompts.length > 0) {
            sendPicks(createPlayerBetDTOWithHorseRacesPicks(state));
        } else {
            sendPicks(createPlayerBetDTO(state));
        }
    };

    return <GameView state={state} dispatch={dispatch} sendPicks={handleSendPicks} />;
};

export type GameState = {
    choice: SubmitPicks;
    choices: SubmitPicks[];
    horseRacePick: SubmitHorseRacePicks;
    horseRaceChoices: SubmitHorseRacePicks[];
    randomButtonPressed: boolean;
    favoritesButtonPressed: boolean;
};

export type GameActions =
    | { type: "setSelection"; choice: SubmitPicks; index: number }
    | { type: "setChoices"; choices: SubmitPicks[] }
    | { type: "setHorsePickemSelection"; selection: number; promptIndex: number }
    | { type: "setHorseSimpleSelection"; selection: number; promptIndex: number }
    | { type: "setHorseRaceChoices"; horseRaceChoices: SubmitHorseRacePicks[] }
    | { type: "setButtonState"; random: boolean; favourites: boolean }
    | { type: "clearPicks" };

const picksReducer = (state: GameState, action: GameActions): GameState => {
    switch (action.type) {
        case "setSelection": {
            const choices = [...state.choices];
            choices[action.index] = action.choice;
            return {
                ...state,
                choices,
                randomButtonPressed: false,
                favoritesButtonPressed: false,
            };
        }
        case "setHorsePickemSelection": {
            const horseRaceChoices = [...state.horseRaceChoices];
            const newChoices = horseRaceChoices[action.promptIndex]?.choices ?? [];
            if (newChoices.includes(action.selection)) {
                newChoices.splice(newChoices.indexOf(action.selection), 1);
            } else {
                newChoices.push(action.selection);
            }
            horseRaceChoices[action.promptIndex] = { choices: newChoices };
            return { ...state, horseRaceChoices, randomButtonPressed: false, favoritesButtonPressed: false };
        }
        case "setHorseSimpleSelection": {
            const horseRaceChoices = [...state.horseRaceChoices];
            horseRaceChoices[action.promptIndex] = { choices: [action.selection] };
            return { ...state, horseRaceChoices, randomButtonPressed: false, favoritesButtonPressed: false };
        }
        case "setHorseRaceChoices": {
            return { ...state, horseRaceChoices: action.horseRaceChoices };
        }
        case "setChoices": {
            return { ...state, choices: action.choices };
        }

        case "setButtonState": {
            return {
                ...state,
                randomButtonPressed: action.random,
                favoritesButtonPressed: action.favourites,
            };
        }
        case "clearPicks":
            return {
                ...state,
                choices: [],
                randomButtonPressed: false,
                favoritesButtonPressed: false,
            };
    }
};
